import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { ParzelleForCreation } from '../../models/parzelleForCreation.model';
import { ParzelleForUpdate } from '../../models/parzelleForUpdate.model';
import { Parzelle, ParzelleLinkOption } from '../../models/parzelle.model';
import { Sektor } from '../../models/sektor.model';
import { SektorForCreate } from '../../models/sektorForCreate.model';

@Component({
    selector: 'app-parzelle-dialog',
    templateUrl: './parzelle-dialog.component.html',
    standalone: false
})
export class ParzelleDialogComponent implements OnChanges {
    @Input() visible: boolean = false;
    @Input() mode: 'create' | 'edit' = 'create';
    @Input() parzelle: ParzelleForCreation | ParzelleForUpdate;
    @Input() allSektoren: SelectItem[] = [];
    @Input() selectedSektor: Sektor;
    @Input() allowSektorCreate = false;
    @Input() parzellenWithoutCoordsOptions: ParzelleLinkOption[] = [];

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() selectedSektorChange = new EventEmitter<Sektor>();
    @Output() onSave = new EventEmitter<ParzelleForCreation | ParzelleForUpdate>();
    @Output() onLinkExisting = new EventEmitter<Parzelle>();
    @Output() onDelete = new EventEmitter<void>();
    @Output() onCreateSektor = new EventEmitter<SektorForCreate>();

    linkExisting = false;
    selectedLinkOption: ParzelleLinkOption;

    displayCreateSektorDialog = false;
    sektorToCreate: SektorForCreate = { name: '', shortname: '', onlyUnevenNumbers: false, onlyEvenNumbers: false };
    numberingTypes: SelectItem[] = [
        { label: 'Alle', value: 0 },
        { label: 'Ungerade', value: 1 },
        { label: 'Gerade', value: 2 }
    ];
    selectedNumberingType = 0;
    numberingExamples = {
        0: [1, 2, 3],
        1: [1, 3, 5],
        2: [2, 4, 6]
    };

    get dialogHeader(): string {
        if (this.mode === 'edit') {
            return 'Parzelle aktualisieren';
        }
        return this.linkExisting ? 'Parzelle zuordnen' : 'Parzelle erstellen';
    }

    get saveButtonLabel(): string {
        if (this.mode === 'edit') {
            return 'Aktualisieren';
        }
        return this.linkExisting ? 'Zuordnen' : 'Erstellen';
    }

    get showLinkExistingOption(): boolean {
        return this.mode === 'create' && this.parzellenWithoutCoordsOptions.length > 0;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.visible?.currentValue === true && this.mode === 'create') {
            this.resetForCreate();
        }
        if (changes.parzellenWithoutCoordsOptions && this.visible && this.mode === 'create') {
            this.selectedLinkOption = this.parzellenWithoutCoordsOptions[0] ?? null;
        }
    }

    private resetForCreate(): void {
        this.linkExisting = false;
        this.selectedLinkOption = this.parzellenWithoutCoordsOptions[0] ?? null;
    }

    isValid(): boolean {
        if (this.mode === 'create' && this.linkExisting) {
            return !!this.selectedLinkOption?.parzelle;
        }

        const p = this.parzelle;
        return !!p
            && p.name?.trim().length >= 2
            && p.kurzname?.trim().length >= 2
            && p.laufmeter >= 0.5
            && (p.tiefe == null || p.tiefe >= 0)
            && (p.abstandBetween == null || p.abstandBetween >= 0)
            && (this.mode === 'edit' || !!this.selectedSektor);
    }

    onSektorChange(): void {
        this.selectedSektorChange.emit(this.selectedSektor);
    }

    showCreateSektorDialog(): void {
        this.sektorToCreate = { name: '', shortname: '', onlyUnevenNumbers: false, onlyEvenNumbers: false };
        this.selectedNumberingType = 0;
        this.displayCreateSektorDialog = true;
    }

    canCreateSektor(): boolean {
        return !!this.sektorToCreate?.name?.trim() && !!this.sektorToCreate?.shortname?.trim();
    }

    createSektor(): void {
        if (!this.canCreateSektor()) {
            return;
        }
        this.sektorToCreate.onlyUnevenNumbers = this.selectedNumberingType === 1;
        this.sektorToCreate.onlyEvenNumbers = this.selectedNumberingType === 2;
        this.onCreateSektor.emit(this.sektorToCreate);
        this.displayCreateSektorDialog = false;
    }

    updateSektorShortname(sektorName: string): void {
        if (sektorName.length >= 2 && !this.sektorToCreate.shortname) {
            this.sektorToCreate.shortname = sektorName.substr(0, 2).toUpperCase();
        }
    }

    save(): void {
        if (!this.isValid()) {
            return;
        }
        if (this.mode === 'create' && this.linkExisting) {
            this.onLinkExisting.emit(this.selectedLinkOption.parzelle);
            return;
        }
        this.onSave.emit(this.parzelle);
    }

    deleteParzelle(): void {
        this.onDelete.emit();
    }

    close(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }
}
